import React, { useEffect, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  Collapse,
  Paper,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../ui/LanguageSelector";
import { INTRANET_URL } from "../../../../config/constants";
const SHOW_BLOG = process.env.REACT_APP_SHOW_BLOG === "true";

interface SubService {
  label: string;
  to: string;
}

interface MenuItemType {
  label: string;
  to: string;
  submenu?: SubService[];
}

export default function Encabezado() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [openServices, setOpenServices] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 18);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const subServicios: SubService[] = [
    { label: t("nav.servicios_list.desarrollo"), to: "/servicios#desarrollo" },
    { label: t("nav.servicios_list.migracion"), to: "/servicios#migracion" },
    { label: t("nav.servicios_list.digitalizacion"), to: "/servicios#digitalizacion" },
    { label: t("nav.servicios_list.data"), to: "/servicios#data" },
    { label: t("nav.servicios_list.incident"), to: "/servicios#incident" },
    { label: t("nav.servicios_list.consultoria"), to: "/servicios#consultoria" },
    { label: t("nav.servicios_list.ia"), to: "/servicios#ia" },
  ];

  const menuItems: MenuItemType[] = [
    { label: t("nav.inicio"), to: "/" },
    { label: t("nav.nosotros"), to: "/empresa" },
    { label: t("nav.servicios"), to: "/servicios", submenu: subServicios },
    { label: t("nav.project_delivery_teams"), to: "/equipos-especializados" },
    ...(SHOW_BLOG ? [{ label: t("nav.blog"), to: "/blog" }] : []),
    { label: t("nav.postulate"), to: "/postulate" },
    { label: t("nav.contacto"), to: "/contacto" },
  ];

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: isScrolled ? "rgba(255, 255, 255, 0.95)" : "#ffffff",
        borderBottom: isScrolled
          ? "1px solid #e2e8f0"
          : "1px solid transparent",
        boxShadow: isScrolled
          ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
          : "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        backdropFilter: isScrolled ? "blur(12px)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar
        sx={{
          maxWidth: "1280px",
          width: "100%",
          mx: "auto",

          display: "grid",
          gridTemplateColumns: { xs: "1fr auto", md: "1fr auto 1fr" },

          alignItems: "center",
          px: { xs: 3, md: 4 },
          minHeight: "72px !important",
        }}
      >
        {/* LOGO */}
        <Box
          onClick={() => navigate("/")}
          sx={{ cursor: "pointer", display: "flex", alignItems: "center" }}
        >
          <img src="/logo20_2.webp" style={{ height: 52 }} />
        </Box>

        {/* DESKTOP MENU */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            backgroundColor: "rgba(248, 250, 252, 0.5)",
            borderRadius: "999px",
            p: 0.5,
            border: "1px solid #f1f5f9",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
            backdropFilter: "blur(4px)",
            gap: { md: 0.25, lg: 0.5 },
          }}
        >
          {menuItems.map((item) => {
            const isActive =
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to);
            const isLongLabel = item.to === "/equipos-especializados";
            const navButtonSx = {
              color: isActive ? "#0069a7" : "#475569",
              fontWeight: 500,
              fontSize: isLongLabel
                ? { md: "0.7rem", lg: "0.75rem" }
                : { md: "0.8rem", lg: "0.875rem" },
              textTransform: "none" as const,
              whiteSpace: isLongLabel ? "normal" : "nowrap",
              textAlign: isLongLabel ? ("center" as const) : undefined,
              lineHeight: isLongLabel ? 1.2 : 1.25,
              maxWidth: isLongLabel ? { md: 112, lg: 128 } : undefined,
              borderRadius: "999px",
              px: isLongLabel ? { md: 1, lg: 1.25 } : { md: 1.25, lg: 2 },
              py: isLongLabel ? 0.65 : 1,
              minWidth: "unset",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#ffffff",
                color: "#0069a7",
              },
            };

            if (item.submenu) {
              return (
                <Box
                  key={item.label}
                  onMouseEnter={() => setOpenServices(true)}
                  onMouseLeave={() => setOpenServices(false)}
                  sx={{ position: "relative" }}
                >
                  <Button
                    onClick={() => navigate("/servicios")}
                    sx={navButtonSx}
                  >
                    {item.label}
                  </Button>

                  <Collapse in={openServices}>
                    <Paper
                      onMouseEnter={() => setOpenServices(true)}
                      onMouseLeave={() => setOpenServices(false)}
                      sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        mt: 1,
                        minWidth: 288,
                        borderRadius: "12px",
                        border: "1px solid #f1f5f9",
                        boxShadow:
                          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        overflow: "hidden",
                        zIndex: 10,
                        py: 1,
                      }}
                    >
                      {item.submenu.map((sub) => (
                        <Box
                          key={sub.label}
                          onClick={() => navigate(sub.to)}
                          sx={{
                            px: 3,
                            py: 1.5,
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "#475569",
                            cursor: "pointer",
                            transition: "colors 0.3s",
                            "&:hover": {
                              backgroundColor: "#f8fafc",
                              color: "#0069a7",
                            },
                          }}
                        >
                          {sub.label}
                        </Box>
                      ))}
                    </Paper>
                  </Collapse>
                </Box>
              );
            }

            return (
              <Button
                key={item.label}
                onClick={() => navigate(item.to)}
                sx={{
                  ...navButtonSx,
                  "&:hover": { backgroundColor: "#ffffff", color: "#0069a7" },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Box>

        {/* LANGUAGE + ACCESO EMPLEADOS DESKTOP */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
            justifySelf: "end",
            mr: 6,
          }}
        >
          <Button
            component="a"
            href={INTRANET_URL}
            target="_blank"
            rel="noopener noreferrer"
            disableRipple
            variant="outlined"
            sx={{
              color: "#0069a7",
              backgroundColor: "#FFFFFF",
              border: "1px solid #0069a7",
              fontWeight: 600,
              fontSize: "0.75rem",
              textTransform: "none",
              borderRadius: "999px",
              height: "34px",
              minHeight: "34px",
              px: 2,
              py: 0,
              lineHeight: 1,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(0, 105, 167, 0.08)",
                border: "1px solid #0069a7",
              },
            }}
          >
            {t("nav.acceso_empleados")}
          </Button>
          <LanguageSelector />
        </Box>

        {/* MOBILE MENU ICON */}
        <Box sx={{ display: { xs: "flex", md: "none" }, mr: 5 }}>
          <IconButton onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>

      {/* ================= MOBILE DRAWER ================= */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <List>
            {menuItems.map((item) => {
              if (item.submenu) {
                return (
                  <Box key={item.label}>
                    <ListItemButton
                      onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    >
                      <ListItemText primary={item.label} />
                      {mobileServicesOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>

                    <Collapse in={mobileServicesOpen}>
                      {item.submenu.map((sub) => (
                        <ListItemButton
                          key={sub.label}
                          onClick={() => {
                            navigate(sub.to);
                            setMobileOpen(false);
                          }}
                          sx={{ pl: 4 }}
                        >
                          <ListItemText primary={sub.label} />
                        </ListItemButton>
                      ))}
                    </Collapse>
                  </Box>
                );
              }

              return (
                <ListItemButton
                  key={item.label}
                  onClick={() => {
                    navigate(item.to);
                    setMobileOpen(false);
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}

            <Box
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                px: 1,
              }}
            >
              <LanguageSelector />
              <Button
                component="a"
                href={INTRANET_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
                disableRipple
                variant="outlined"
                sx={{
                  color: "#0069a7",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #0069a7",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: "999px",
                  height: "34px",
                  minHeight: "34px",
                  px: 2,
                  py: 0,
                  lineHeight: 1,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(0, 105, 167, 0.08)",
                    border: "1px solid #0069a7",
                  },
                }}
              >
                {t("nav.acceso_empleados")}
              </Button>
            </Box>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
}
